import { useEffect, useMemo, useRef, useState } from "react";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { TYPE_META, type Item } from "@/lib/types";
import { StatusPill } from "./atoms";
import { SearchIcon } from "./icons";

const MAX_HITS = 8;

/**
 * §14 — the header search covers titles AND item notes across every project,
 * not just whatever the current view happens to hold.
 *
 * The dropdown is anchored `left: 0`, deliberately. The pill is 260px and the
 * panel is 340px, so right-anchoring would overhang 80px to the left and get
 * clipped by the content column's `overflow: hidden` — the column is the thing
 * that clips, not the viewport.
 */
export function GlobalSearch({ query, setQuery }: { query: string; setQuery: (q: string) => void }) {
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { data: projects = [] } = useProjects();
  const { openItem } = useTaskEditor();
  const [focused, setFocused] = useState(false);
  // Whether the field is expanded. Distinct from `open` below, which is
  // whether the RESULTS dropdown is showing.
  const [expanded, setExpanded] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const phaseById = useMemo(() => new Map(phases.map((p) => [p.id, p])), [phases]);
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return items.filter(
      (i) => i.title.toLowerCase().includes(q) || (i.notes || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const open = focused && query.trim().length > 0;

  // "/" opens search from anywhere, unless you are typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))) return;
      e.preventDefault();
      setExpanded(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function where(item: Item): string {
    const phase = phaseById.get(item.phase);
    const project = phase ? projectById.get((phase as { project?: string }).project || "") : undefined;
    return [project?.name, phase?.name].filter(Boolean).join(" · ");
  }

  return (
    /**
     * A small field until you want it.
     *
     * A permanent 260px field is a lot of header for something reached
     * occasionally. Collapsed, it is a field-shaped pill reading "Search" with
     * a magnifying glass — it was a bare 34px circle with a ⌕ glyph, which
     * didn't read as search at all. Open, it is a popover that can be as wide
     * as it likes because it floats over the page. "/" opens it from anywhere.
     */
    <div ref={boxRef} style={{ position: "relative", flex: "none" }}>
      {!expanded ? (
        <button
          onClick={() => setExpanded(true)}
          aria-label="Search all projects"
          title="Search all projects (/)"
          style={{
            height: 34, width: 132, borderRadius: 999, border: "1px solid var(--line2)",
            background: "var(--surface)", color: "var(--muted)", cursor: "text",
            display: "flex", alignItems: "center", gap: 8, padding: "0 12px", fontSize: 13,
          }}
        >
          <SearchIcon size={16} />
          Search
        </button>
      ) : (
      <div style={{
        position: "absolute", right: 0, top: -4, zIndex: 32,
        display: "flex", alignItems: "center", gap: 8, padding: "0 12px", height: 38, borderRadius: 999,
        border: "1px solid var(--line2)", background: "var(--surface)", width: 300, maxWidth: "70vw",
        boxShadow: "var(--shadowHover)",
      }}>
        <span style={{ color: "var(--muted)", display: "inline-flex" }}><SearchIcon size={16} /></span>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => { if (e.key === "Escape") { setQuery(""); setExpanded(false); e.currentTarget.blur(); } }}
          placeholder="Search all projects…"
          style={{ flex: 1, minWidth: 0, border: "none", background: "none", outline: "none", fontSize: 13 }}
        />
        <button
          onClick={() => { setQuery(""); setExpanded(false); }}
          aria-label={query ? "Clear search" : "Close search"}
          style={{ border: "none", background: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 13, padding: 0 }}
        >✕</button>
      </div>
      )}

      {open && (
        <>
          <div onClick={() => setFocused(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
          <div style={{
            // Anchored to the right edge, like the field above it: the search
            // sits at the far right of the header, and a list hung off its
            // left edge ran past the window.
            // Fixed offset, not 100%: the open field floats (absolute, top -4,
            // 38 tall), so this wrapper has no height to measure from.
            position: "absolute", right: 0, top: 42, zIndex: 31, width: 340, maxWidth: "90vw",
            background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14,
            boxShadow: "var(--shadowHover)", overflow: "hidden",
          }}>
            <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--tint2)", fontSize: 11.5, color: "var(--muted)" }}>
              {hits.length === 0
                ? "No matches"
                : `${hits.length} match${hits.length === 1 ? "" : "es"}${hits.length > MAX_HITS ? ` · showing ${MAX_HITS}` : ""}`}
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {hits.slice(0, MAX_HITS).map((it) => (
                <button
                  key={it.id}
                  onClick={() => { setFocused(false); openItem(it); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left",
                    padding: "9px 12px", border: "none", borderBottom: "1px solid var(--tint2)",
                    background: "transparent", cursor: "pointer",
                  }}
                >
                  <span style={{ color: TYPE_META[it.type].color, fontSize: 12, flex: "none" }}>{TYPE_META[it.type].glyph}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                    <span style={{ display: "block", fontSize: 11.5, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{where(it)}</span>
                  </span>
                  <StatusPill status={it.status} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
